import { useState, useMemo } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpDownIcon, PlusIcon, SearchIcon } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface Column<T> {
  key: string;
  header: string;
  cell: (item: T) => React.ReactNode;
  className?: string;
  sortable?: boolean;
  sortValue?: (item: T) => string | number | boolean | Date | null | undefined;
  sortCompare?: (a: T, b: T) => number;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  caption?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: (keyof T)[];
  actions?: React.ReactNode;
  onCreate?: () => void;
  createLabel?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  pageSize?: number;
}

type SortDirection = 'asc' | 'desc';

type SortState = {
  key: string;
  direction: SortDirection;
} | null;

export function DataTable<T>({
  data,
  columns,
  caption,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchKeys,
  actions,
  onCreate,
  createLabel = 'Create',
  emptyMessage = 'No data found',
  isLoading = false,
  pageSize = 20,
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortState, setSortState] = useState<SortState>(null);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery || !searchKeys || searchKeys.length === 0) {
      return data;
    }
    const query = searchQuery.toLowerCase();
    return data.filter((item) =>
      searchKeys.some((key) => {
        const value = item[key];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(query);
        }
        if (typeof value === 'number') {
          return value.toString().includes(query);
        }
        return false;
      })
    );
  }, [data, searchQuery, searchKeys]);

  const sortedData = useMemo(() => {
    if (!sortState) {
      return filteredData;
    }

    const activeColumn = columns.find((column) => column.key === sortState.key);
    if (!activeColumn || !activeColumn.sortable) {
      return filteredData;
    }

    const sorted = [...filteredData].sort((a, b) => {
      const directionMultiplier = sortState.direction === 'asc' ? 1 : -1;

      if (activeColumn.sortCompare) {
        return activeColumn.sortCompare(a, b) * directionMultiplier;
      }

      const fallbackA = (a as Record<string, unknown>)[activeColumn.key];
      const fallbackB = (b as Record<string, unknown>)[activeColumn.key];
      const rawA = activeColumn.sortValue
        ? activeColumn.sortValue(a)
        : fallbackA instanceof Date || typeof fallbackA === 'string' || typeof fallbackA === 'number' || typeof fallbackA === 'boolean'
          ? fallbackA
          : null;
      const rawB = activeColumn.sortValue
        ? activeColumn.sortValue(b)
        : fallbackB instanceof Date || typeof fallbackB === 'string' || typeof fallbackB === 'number' || typeof fallbackB === 'boolean'
          ? fallbackB
          : null;

      if (rawA == null && rawB == null) {
        return 0;
      }
      if (rawA == null) {
        return 1;
      }
      if (rawB == null) {
        return -1;
      }

      if (rawA instanceof Date && rawB instanceof Date) {
        return (rawA.getTime() - rawB.getTime()) * directionMultiplier;
      }

      if (typeof rawA === 'number' && typeof rawB === 'number') {
        return (rawA - rawB) * directionMultiplier;
      }

      if (typeof rawA === 'boolean' && typeof rawB === 'boolean') {
        return ((rawA === rawB ? 0 : rawA ? 1 : -1) * directionMultiplier);
      }

      return String(rawA).localeCompare(String(rawB), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * directionMultiplier;
    });

    return sorted;
  }, [columns, filteredData, sortState]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSortToggle = (column: Column<T>) => {
    if (!column.sortable) {
      return;
    }

    setCurrentPage(1);
    setSortState((current) => {
      if (!current || current.key !== column.key) {
        return {
          key: column.key,
          direction: 'asc',
        };
      }

      if (current.direction === 'asc') {
        return {
          key: column.key,
          direction: 'desc',
        };
      }

      return null;
    });
  };

  const renderSortIcon = (column: Column<T>) => {
    if (!column.sortable) {
      return null;
    }

    if (sortState?.key !== column.key) {
      return <ArrowUpDownIcon className="size-3.5 text-muted-foreground" />;
    }

    if (sortState.direction === 'asc') {
      return <ArrowUpIcon className="size-3.5" />;
    }

    return <ArrowDownIcon className="size-3.5" />;
  };

  // Generate pagination numbers
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-6 shadow-sm space-y-6">
        <div className="flex items-center justify-between gap-4">
          {searchable && (
            <div className="relative flex-1 max-w-sm">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {actions}
            {onCreate && (
              <Button size="sm" onClick={onCreate}>
                <PlusIcon className="mr-2 size-4" />
                {createLabel}
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border bg-card">
          <Table>
            {caption && <TableCaption>{caption}</TableCaption>}
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key} className={cn(column.className)}>
                    {column.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSortToggle(column)}
                        className="inline-flex items-center gap-1.5 font-semibold text-foreground transition hover:text-foreground/80"
                      >
                        {column.header}
                        {renderSortIcon(column)}
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((item, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell key={column.key} className={cn(column.className)}>
                        {column.cell(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
            {filteredData.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of{' '}
                    {filteredData.length} entries
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className={
                    currentPage === 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {getPageNumbers().map((page, index) => (
                <PaginationItem key={index}>
                  {typeof page === 'string' ? (
                    <PaginationEllipsis />
                  ) : (
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className={
                    currentPage === totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
